import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { auth } from '../lib/api';
import { useAuthStore } from '../store/useStore';
import { trackEvent } from '../lib/analytics';
import AuthFrame from '../components/AuthFrame';

export default function Register() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const { user, token } = await auth.register({
        username,
        email,
        password,
        displayName: displayName || undefined,
        inviteCode,
      });
      setAuth(user, token);
      trackEvent('register');
      toast.success('Account created');
      navigate('/read');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthFrame kicker="Register" title="Join with an invite.">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="inviteCode" className="label">Invite code</label>
          <input
            id="inviteCode"
            type="text"
            required
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            placeholder="SUPER-DRAGON-ROCKS-42"
            className="field field-mono"
          />
          <p className="kicker mt-1">Ask a member for one</p>
        </div>

        <div>
          <label htmlFor="username" className="label">Username</label>
          <input
            id="username"
            type="text"
            required
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            pattern="[a-zA-Z0-9_-]+"
            title="Letters, numbers, hyphens, and underscores"
            className="field"
          />
        </div>

        <div>
          <label htmlFor="email" className="label">Email</label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="field"
          />
        </div>

        <div>
          <label htmlFor="displayName" className="label">Display name · optional</label>
          <input
            id="displayName"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="field"
          />
        </div>

        <div>
          <label htmlFor="password" className="label">Password</label>
          <input
            id="password"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="field"
          />
          <p className="kicker mt-1">At least 8 characters</p>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="label">Confirm password</label>
          <input
            id="confirmPassword"
            type="password"
            required
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="field"
          />
        </div>

        <button type="submit" disabled={loading} className="btn btn-accent w-full">
          {loading ? 'Creating account…' : 'Create account'}
        </button>
      </form>

      <p className="mt-6 text-sm text-ink-muted">
        Already a member?{' '}
        <Link to="/login" className="text-ink underline underline-offset-2 hover:text-accent-600">
          Sign in
        </Link>
      </p>
    </AuthFrame>
  );
}
